import { createAsyncThunk } from "@reduxjs/toolkit";
import { generateGuid, setTokenToStorage, setPartnerIdToStorage, removePartnerIdFromStorage } from "@/utils";
import { AuthDataType, IBitrixRegistrationUserDataType, IRespData, IRegValuesType, IRegResponseType, IUpdateUserDataType, IBitrixResponseType } from "./types";
import { authRoutes } from "./routes";
import { axiosBitrixInstance, axiosInstance } from "@/resources/requestInfo";
import { hashTokenHelper } from "@/utils/hashToken";

export const fetchAuth = createAsyncThunk<IRespData, AuthDataType>(
  "authAndReg/fetchAuth",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(authRoutes.AUTH, data);

      const token = response.data.access_token;
      const partnerId = response.data.partner_id;

      setTokenToStorage(token);

      // Если есть partner_id в ответе, шифруем и сохраняем его
      if (partnerId) {
        setPartnerIdToStorage(partnerId);
      }

      return {
        auth: token,
        showWindow: undefined,
        partner_id: partnerId,
      };
    } catch (error: any) {
      return rejectWithValue(error.response.data.message);
    }
  }
);

export const fetchLogout = createAsyncThunk<string, void>(
  "authAndReg/fetchLogout",
  async (_, { rejectWithValue }) => {
    try {
      // Новый API: GET /auth/logout (вместо POST)
      const response = await axiosInstance.get(authRoutes.LOGOUT);
      
      // Удаляем зашифрованный partner_id из localStorage при выходе
      removePartnerIdFromStorage();

      return response.status.toString();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Ошибка выхода");
    }
  } 
);

export const fetchReg = createAsyncThunk<string, IRegValuesType>(
  "authAndReg/fetchReg",
  async (data, { rejectWithValue }) => {
    try{ 
      const hashToken = await hashTokenHelper();
      
      const response = await axiosInstance.post(authRoutes.REG, data, {
        headers: {
          "Authorization": `Bearer ${hashToken}`,
        },
      });

      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Ошибка регистрации";
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchRequestCode = createAsyncThunk<string, IRegResponseType>(
  "reg/fetchReqCode",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(authRoutes.CONFIRM_SIGNUP, data);

      return response.data.access_token;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || "Ошибка подтверждения кода";
      return rejectWithValue(errorMessage);
    }
    
  }
);

export const FetchBitrixRegistrationUser  = createAsyncThunk<string, IBitrixRegistrationUserDataType>(
  "reg/fetchBitrixRegistrationUser",
  async (data, { rejectWithValue }) => {
    try {
      let nameTemp = [];

      const guid = generateGuid(data);
      const changeDate = new Date().toISOString();

      if (data.name.split(' ').length > 1) {
        nameTemp = data.name.split(' ')
      } else {
        nameTemp[0] = data.name
        nameTemp[1] = ''
      }

      const bitrixData = {
        name: nameTemp[0],
        surname: nameTemp[1],
        phone: data.phone,
        email: data.email,
        guid: guid,
        changeDate: changeDate,
        // Новый пользователь — не тренер; ранний доступ выдаётся в админке (П1328)
        isCoach: false,
      }

      const response = await axiosBitrixInstance.post<IBitrixResponseType>(authRoutes.BITRIX_REGISTRATION_USER, bitrixData);
    
      const partnerId = response.data.partnerId;
      
      if (!partnerId) {
        return rejectWithValue("Ошибка регистрации: не удалось получить partner_id");
      }
      
      // Шифруем и сохраняем partner_id в localStorage
      setPartnerIdToStorage(partnerId);
      
      return partnerId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Ошибка регистрации в Bitrix");
    }
  }
);

export const fetchUpdateUser = createAsyncThunk<any, IUpdateUserDataType>(
  "authAndReg/fetchUpdateUser",
  async (data, { rejectWithValue }) => {
    try {
      // Если есть partner_id, шифруем и сохраняем его
      if (data.partner_id) {
        setPartnerIdToStorage(data.partner_id);
      }
      
      const response = await axiosInstance.put(authRoutes.UPDATE_USER, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Ошибка обновления информации пользователя");
    }
  }
);